import { 
  Percent, 
  DollarSign, 
  Receipt, 
  Gift, 
  TrendingUp,
  Building2,
  Users
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import type { Brokerage } from '@/contracts/brokerage';
import { formatCurrency, formatSplit } from '@/contracts/brokerage';

interface BrokerageAtAGlanceProps {
  brokerage: Brokerage;
  className?: string;
}

export function BrokerageAtAGlance({ brokerage, className }: BrokerageAtAGlanceProps) {
  const items = [
    {
      icon: Percent,
      label: 'Commission Split',
      value: formatSplit(brokerage.split_starting_percent, brokerage.split_max_percent),
      highlight: true,
    },
    {
      icon: DollarSign,
      label: 'Annual Cap',
      value: brokerage.cap_amount ? formatCurrency(brokerage.cap_amount) : 'No Cap',
      subtext: brokerage.cap_amount ? `per ${brokerage.cap_period || 'year'}` : undefined,
    },
    {
      icon: Receipt,
      label: 'Monthly Fees',
      value: brokerage.monthly_fee ? formatCurrency(brokerage.monthly_fee) : '$0',
      subtext: brokerage.desk_fee_optional ? '(optional)' : '/month',
    },
    {
      icon: Receipt,
      label: 'Per-Transaction',
      value: brokerage.transaction_fee 
        ? (brokerage.transaction_fee_type === 'percent' 
            ? `${brokerage.transaction_fee}%` 
            : formatCurrency(brokerage.transaction_fee))
        : '$0',
    },
    {
      icon: Gift,
      label: 'Sign-on Bonus',
      value: brokerage.sign_on_bonus_max 
        ? `Up to ${formatCurrency(brokerage.sign_on_bonus_max)}`
        : 'None listed',
      highlight: !!brokerage.sign_on_bonus_max,
    },
  ];

  // Add revenue share / stock if available
  const bonuses: string[] = [];
  if (brokerage.has_revenue_share) bonuses.push('Revenue Share');
  if (brokerage.has_stock_awards) bonuses.push('Stock Awards');

  return (
    <Card className={className}>
      <CardHeader className="pb-2">
        <CardTitle className="text-lg flex items-center gap-2">
          <Building2 className="h-5 w-5 text-primary" />
          At a Glance
        </CardTitle>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {items.map((item, index) => (
            <div 
              key={index}
              className={`rounded-lg p-3 text-center ${
                item.highlight 
                  ? 'bg-primary/5 border border-primary/20' 
                  : 'bg-muted/50'
              }`}
            >
              <item.icon className={`h-5 w-5 mx-auto mb-1.5 ${
                item.highlight ? 'text-primary' : 'text-muted-foreground'
              }`} />
              <p className="text-xs text-muted-foreground mb-1">{item.label}</p>
              <p className={`font-bold ${
                item.highlight ? 'text-primary text-lg' : 'text-foreground'
              }`}>
                {item.value}
              </p>
              {item.subtext && (
                <p className="text-xs text-muted-foreground">{item.subtext}</p>
              )}
            </div>
          ))}
        </div>

        {bonuses.length > 0 && (
          <div className="mt-3 flex items-center gap-2 flex-wrap">
            <TrendingUp className="h-4 w-4 text-emerald-600" />
            <span className="text-sm text-muted-foreground">Additional:</span>
            {bonuses.map((bonus) => (
              <Badge 
                key={bonus} 
                variant="secondary" 
                className="bg-emerald-50 text-emerald-700 border-emerald-200"
              >
                {bonus}
              </Badge>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
}
